import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

export const AdminAuthContext = createContext(null);
const STORAGE_KEY = 'changeme';
const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function AdminAuthProvider({ children }) {
  const [secret, setSecret] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (secret) localStorage.setItem(STORAGE_KEY, secret);
    else localStorage.removeItem(STORAGE_KEY);
  }, [secret]);

  // Mark as ready once the initial secret is loaded
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Validate the admin secret by making a test API call to a PROTECTED endpoint
  const login = async (s) => {
    if (!s || !s.trim()) {
      throw new Error('Admin secret is required');
    }

    try {
      // Test the secret by calling a PROTECTED endpoint (sessions list requires admin auth)
      const res = await fetch(`${API_ROOT}/api/sessions?tenantId=demo-university&limit=1`, {
        headers: {
          'x-admin-secret': s.trim()
        }
      });

      if (!res.ok) {
        if (res.status === 403 || res.status === 401) {
          throw new Error('Invalid admin secret');
        }
        throw new Error(`Server error: ${res.status}`);
      }

      // If successful, store the secret
      setSecret(s.trim());
    } catch (err) {
      throw new Error(err.message || 'Authentication failed');
    }
  };

  const value = useMemo(() => ({
    isAuthed: Boolean(secret),
    secret,
    isReady,
    login,
    logout: () => setSecret('')
  }), [secret, isReady]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}